import { FileUploader } from '@expressable/syntax-ds';
import hipaaLogo from 'assets/images/hipaa-logo.png';
import { Form, FormField, StackedRadioCardGroup, SubmitButton, TextAreaInput } from 'components/Forms';
import { Heading, Paragraph } from 'components/Typography';
import { usePresignedUploadFunction } from 'domains/uploads/presigned/data';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ASSIGNMENT_FEEDBACK_FORM_OPTIONS, FormData, getLocalizedSchema } from './schema';

type Props = {
  onSubmit: SubmitHandler<FormData>;
  previewMode?: boolean;
};

export const AssignmentFeedbackForm = ({ onSubmit, previewMode }: Props): JSX.Element => {
  const { t } = useTranslation();

  const uploadFn = usePresignedUploadFunction();
  return (
    <>
      <Form
        schema={getLocalizedSchema(t)}
        onSubmit={onSubmit}
        className="flex flex-col gap-40 w-full"
        flashErrors={false}
        disabled={previewMode}
      >
        <section>
          <Heading level="h3" useDefaultMargins>
            {t('assignments.feedbackForm.title')}
          </Heading>
          <Paragraph useDefaultMargins>{t('assignments.feedbackForm.subtitle')}</Paragraph>
        </section>

        <FormField
          component={StackedRadioCardGroup}
          options={ASSIGNMENT_FEEDBACK_FORM_OPTIONS.activitySuccessOptions}
          name="activitySuccess"
          label={t('assignments.feedbackForm.label.activitySuccess')}
          tFunc={t}
          tPrefixKey="assignments.feedbackForm.activitySuccess.selectOption"
        />
        <FormField
          component={StackedRadioCardGroup}
          options={ASSIGNMENT_FEEDBACK_FORM_OPTIONS.difficultyOptions}
          name="difficulty"
          label={t('assignments.feedbackForm.label.difficulty')}
          tFunc={t}
          tPrefixKey="assignments.feedbackForm.difficulty.selectOption"
        />
        <FormField
          component={StackedRadioCardGroup}
          options={ASSIGNMENT_FEEDBACK_FORM_OPTIONS.wouldRepeatOptions}
          name="wouldRepeat"
          label={t('assignments.feedbackForm.label.wouldRepeat')}
          tFunc={t}
          tPrefixKey="assignments.feedbackForm.wouldRepeat.selectOption"
        />
        <FormField
          component={TextAreaInput}
          rows={10}
          name="feedback"
          label={t('assignments.feedbackForm.label.feedback')}
        />

        <FileUploader
          name="files"
          label={t('assignments.feedbackForm.label.files')}
          hint={t('assignments.feedbackForm.label.files.subtitle')}
          acceptedTypeCategories={['image', 'video', 'audio', 'document']}
          uploadFn={uploadFn}
        />

        <div className="flex justify-between items-center sm-d:flex-col sm-d:gap-40">
          <SubmitButton size="lg" className="sm-d:w-full" willRedirect={false}>
            {t('assignments.feedbackForm.submit')}
          </SubmitButton>

          <img alt="" src={hipaaLogo} />
        </div>
      </Form>
    </>
  );
};
